import { LifeglovHand, LifeglovSize } from '@bioliberty/core';
import { IdCard, LoaderCircle } from 'lucide-react';
import { useCallback, useState } from 'react';
import z from 'zod';
import { useAddPatientMutation, useGeneratePatientAliasLazyQuery } from '../../api';
import { useAddPatientLifeglovMutation } from '../../api/lifeglovs';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { InputLabel, TextInput } from '../../components/Input/TextInput';
import { useActiveSubtenants } from '../../hooks/useActiveSubtenants';
import { useToaster } from '../../hooks/useToaster';
import { useValidator } from '../../hooks/useValidator';
import { capitalizeFirstLetter } from '../../util';

type AddPatientModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddPatientModal = ({ open, onOpenChange }: AddPatientModalProps) => {
  const activeSubtenants = useActiveSubtenants();

  const [generateAlias, { isLoading: isLoadingAlias, isFetching: isFetchingAlias }] =
    useGeneratePatientAliasLazyQuery();
  const isGeneratingAlias = isLoadingAlias || isFetchingAlias;

  const [alias, setAlias] = useState('');
  const [generatedAlias, setGeneratedAlias] = useState('');
  const [tenantId, setTenantId] = useState(activeSubtenants?.[0]?.name);
  const [size, setSize] = useState<LifeglovSize>();
  const [hand, setHand] = useState<LifeglovHand>();
  const toast = useToaster();

  const { validate, errors } = useValidator(
    z.object({
      alias: z
        .string()
        .regex(/\S+/, 'Please enter a valid unique patient identifier')
        .min(1, 'A unique patient identifier is required'),
      size: z.nativeEnum(LifeglovSize).optional(),
      hand: z.nativeEnum(LifeglovHand).optional(),
      tenantId: z
        .string()
        .optional()
        .refine((value) => !activeSubtenants?.length || !!value, {
          message: 'A clinic is required.'
        })
    })
  );

  const [addPatient, { isLoading: isPatientCreating }] = useAddPatientMutation();
  const [addPatientLifeglov, { isLoading: isLifeglovCreating }] = useAddPatientLifeglovMutation();

  const handleAliasChange = (e: string) => {
    setAlias(e);
  };

  const resetForm = useCallback(() => {
    setAlias('');
    setGeneratedAlias('');
    setTenantId(activeSubtenants?.[0]?.name);
    setSize(undefined);
    setHand(undefined);
  }, [activeSubtenants]);

  const onCreate = useCallback(async () => {
    if (!validate({ alias, tenantId, size, hand })) {
      return;
    }

    const patient = {
      alias: alias.trim(),
      tenantId
    };

    try {
      const newPatient = await addPatient(patient).unwrap();

      const newPatientLifeglov = {
        patientId: newPatient.id,
        size: size,
        hand: hand
      };

      await addPatientLifeglov(newPatientLifeglov).unwrap();

      toast.success('Patient added successfully');
      onOpenChange(false);
      resetForm();
    } catch (error) {
      const errorTitle = (error as { data?: { title: string } }).data?.title || 'Error adding patient';
      toast.error(errorTitle);
    }
  }, [addPatient, addPatientLifeglov, alias, size, hand, onOpenChange, toast, tenantId, validate, resetForm]);

  const onGenerateAlias = async () => {
    if (!tenantId || isGeneratingAlias) {
      return;
    }

    try {
      const data = await generateAlias({ tenantId }).unwrap();
      if (data.alias) {
        setAlias(data.alias);
        setGeneratedAlias(data.alias);
      }
    } catch {
      toast.error(`Failed to generate a patient alias. Please try again.`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Patient</DialogTitle>
        </DialogHeader>

        <TextInput
          key={generatedAlias}
          initialValue={generatedAlias}
          label={
            <div className="flex items-center justify-between">
              <span>Unique patient identifier*</span>
              <span
                className="text-accent flex cursor-pointer items-center justify-center gap-1 text-sm hover:brightness-110"
                onClick={onGenerateAlias}
              >
                {isGeneratingAlias ? <LoaderCircle size={16} className="animate-spin" /> : <IdCard size={16} />}
                <span>Auto Generate</span>
              </span>
            </div>
          }
          onChange={handleAliasChange}
          errorMessage={errors.alias}
          autoFocus
        />

        {activeSubtenants && activeSubtenants.length > 1 && (
          <div>
            <InputLabel>Clinic*</InputLabel>
            <Select defaultValue={tenantId} onValueChange={setTenantId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a clinic" />
              </SelectTrigger>
              <SelectContent>
                {activeSubtenants.map((subtenant) => (
                  <SelectItem key={subtenant.name} value={subtenant.name}>
                    {subtenant.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <InputLabel>Lifeglov size</InputLabel>
          <Select
            defaultValue={undefined}
            onValueChange={(value) => setSize(value === 'undefined' ? undefined : (value as LifeglovSize))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a Lifeglov size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key={undefined} value="undefined">
                N/A
              </SelectItem>
              {Object.values(LifeglovSize).map((size) => (
                <SelectItem key={size} value={size}>
                  {capitalizeFirstLetter(size)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <InputLabel>Lifeglov hand</InputLabel>
          <Select
            defaultValue={undefined}
            onValueChange={(value) => setHand(value === 'undefined' ? undefined : (value as LifeglovHand))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a Lifeglov hand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem key={undefined} value="undefined">
                N/A
              </SelectItem>
              {Object.values(LifeglovHand).map((hand) => (
                <SelectItem key={hand} value={hand}>
                  {capitalizeFirstLetter(hand)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant="accent"
            onClick={onCreate}
            disabled={isPatientCreating || isLifeglovCreating}
            isLoading={isPatientCreating || isLifeglovCreating}
          >
            Add
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
